import { t } from '../i18n';
import {
  KeyValuePair,
  ExtraPromotion,
  PageTypeEnum,
  CartGift,
  PartOrderDiscount,
  PartOrderBenefit,
  PartOrderPromotion,
  CartSellerLine,
  CartOrderLine,
  CartPromotion,
} from '../types';
import { DeliveryAddressInfo } from '@teko/cart';
import { Address } from '@teko/user';
export * from './common';
export * from './search';
export * from './products';
export * from './order-detail';
export * from './teko-oauth';
export * from './env';
export * from './tracking';
export * from './discovery';
export * from './cart';
export * from './router';
export * from './service-provider';
export * from './screen';
export * from './combo';
export * from './related-promotions';
export * from './menuItem';
export * from './notification';
export * from './navigation-bar';
import { FeatureFlag } from 'types';
import slug from '@teko/slug';
import { formatCurrency } from './common';
import { CartContextState, GiftsImgMap } from 'contexts';
import { PromoItemProps, PromoItemType } from '@teko/ecommerce';
export const transformExtraPromotion = (promotion: any): ExtraPromotion => {
  return {
    ...promotion,
    date: {
      endDate: new Date(promotion.date.endDate).toISOString(),
      startDate: new Date(promotion.date.startDate).toISOString(),
    },
  };
};
export function isEmpty(obj: unknown): boolean {
  return Object.keys(obj).length === 0;
}
export const normalizeParams = (params: KeyValuePair): KeyValuePair => {
  const newParams: KeyValuePair = {};
  Object.keys(params).forEach((key) => {
    if (params[key]) {
      newParams[key] = params[key];
    }
  });
  return newParams;
};
export const createMarkup = (content: string) => {
  return { __html: content };
};
export const formatDateString = (date: string) => {
  if (date.charAt(date.length - 1) == 'Z') {
    return date;
  } else {
    return date.replace(/-/g, '/');
  }
};
export const checkExpiryDate = (
  startDate: string,
  endDate: string,
): boolean => {
  const today = new Date().getTime();
  startDate = formatDateString(startDate);
  endDate = formatDateString(endDate);
  if (
    today - new Date(startDate).getTime() > 0 &&
    new Date(endDate).getTime() - today > 0
  )
    return true;
  return false;
};
export const checkExpiredOrder = (predictCancelAt: any) => {
  if (!predictCancelAt) return false;
  if (typeof predictCancelAt === 'string')
    return (
      new Date(
        /((\+\d{2}:\d{2})|Z)$/.test(predictCancelAt) // unknown timezone 2020-11-03T09:34:45 ???
          ? predictCancelAt
          : `${predictCancelAt}Z`,
      ) < new Date()
    );
  return predictCancelAt < new Date();
};
export const checkInTimeRange = (start: string, end: string): boolean => {
  if (!start || !end) return true;
  const startTime = new Date()
    .toISOString()
    .substring(0, 10)
    .concat(' ', start);
  const endTime = new Date().toISOString().substring(0, 10).concat(' ', end);
  return checkExpiryDate(startTime, endTime);
};
export const getCookieByName = (cname: string) => {
  const match = document.cookie.match(new RegExp('(^| )' + cname + '=([^;]+)'));
  if (match) return match[2];
  return null;
};
export const setCookieByName = (
  cname: string,
  cvalue: string,
  expireTime?: string,
  path?: string,
): void => {
  document.cookie = `${cname}=${cvalue}${
    expireTime ? `;expires=${expireTime}` : ''
  }${path ? `;path=${path}` : ''}`;
};
export const getScrollbarWidth = () => {
  let width = 0;
  if (
    typeof window !== 'undefined' &&
    document.body.scrollHeight > window.innerHeight
  ) {
    if ((window as any).scrollbarWidth) {
      return (window as any).scrollbarWidth;
    }
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    const inner = document.createElement('div');
    outer.appendChild(inner);
    width = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);
    (window as any).scrollbarWidth = width;
  }
  return width;
};

//get element offset relative to the document
export const getElementOffset = (elem: HTMLElement) => {
  const box = elem.getBoundingClientRect();
  const body = document.body;
  const docEl = document.documentElement;
  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;
  const top = box.top + scrollTop - clientTop;
  const left = box.left + scrollLeft - clientLeft;
  return { top, left };
};
export const getDefaultAddressString = ({
  addressLine,
  wardName,
  districtName,
  provinceName,
}: DeliveryAddressInfo): string => {
  return [addressLine, wardName, districtName, provinceName]
    .filter(Boolean)
    .join(', ');
};
export const getAddressString = (address: Address): string => {
  return [
    address.address,
    address.wardName,
    address.districtName,
    address.provinceName,
  ]
    .filter(Boolean)
    .join(', ');
};
export const removeNullField = (obj: any) => {
  const newObj = { ...obj };
  for (const key of Object.keys(newObj)) {
    if (newObj[key] === null || newObj[key] === '') {
      delete newObj.key;
    }
  }
  return newObj;
};
export const normalizeMoneyValue = (value: number) => {
  if (value < 1000000)
    return {
      value: Math.round((value / 1000) * 1000) / 1000,
      unit: 'K',
    };
  if (value > 1000000000)
    return {
      value: Math.round((value / 1000000000) * 1000) / 1000,
      unit: t('Tỷ'),
    };
  return {
    value: Math.round((value / 1000000) * 1000) / 1000,
    unit: 'Tr',
  };
};
export const getPromotionOrderTitle = (
  orderValueMin: number,
  orderValueMax: number,
) => {
  let title = t('Quà tặng đơn hàng');
  const min = orderValueMin ? normalizeMoneyValue(orderValueMin) : undefined;
  const max = orderValueMax ? normalizeMoneyValue(orderValueMax) : undefined;
  if (min && max) {
    if (min.unit === max.unit)
      title += t('từ {{MinValue}} - {{MaxValue}}{{MinUnit}}', {
        MinValue: min.value,
        MaxValue: max.value,
        MinUnit: min.unit,
      });
    else
      title += t('từ {{MinValue}}{{MinUnit}} - {{MaxValue}}{{MaxUnit}}', {
        MinValue: min.value,
        MinUnit: min.unit,
        MaxValue: max.value,
        MaxUnit: max.unit,
      });
  } else if (min)
    title += t('từ {{MinValue}}{{MinUnit}}', {
      MinValue: min.value,
      MinUnit: min.unit,
    });
  else if (max)
    title += t('từ 0 - {{MaxValue}}{{MaxUnit}}', {
      MaxValue: max.value,
      MaxUnit: max.unit,
    });
  return title;
};
export const checkTenantFeatureFlag = (
  feature: string,
  featureFlags: FeatureFlag,
  tenant: string,
): boolean => {
  const tenantFlagName = [tenant, feature].join('_');
  if (featureFlags) {
    return featureFlags[tenantFlagName]
      ? featureFlags[tenantFlagName].enabled
      : !!featureFlags[feature]?.enabled;
  }
  return false;
};
export const stripNewsContent = (string: string): string => {
  return string
    .replace(/<\/?[^>]+>/gi, ' ')
    .replace(/<img[^>"']*((("[^"]*")|('[^']*'))[^"'>]*)*>/g, '')
    .substring(0, 200);
};
export const buildSlugUrl = (
  pageType: PageTypeEnum,
  name: string,
  id: string | number,
) => {
  const trimmedId = id?.toString()?.trim();
  const trimmedName = name?.trim();
  if (!trimmedName || !trimmedName) return null;
  switch (pageType) {
    case PageTypeEnum.CATEGORY:
      return `/${slug(trimmedName)}-cat.${trimmedId}`;
    case PageTypeEnum.BRAND:
      return `/${slug(trimmedName)}-brand.${trimmedId}`;
    case PageTypeEnum.SELLER:
      return `/${slug(trimmedName)}-sel.${trimmedId}`;
    default:
      return null;
  }
};
/**
 *
 * @param promise
 * @param timeout: in milliseconds
 */
export const promiseTimeout = (
  promise: Promise<any>,
  timeoutMillis: number,
): Promise<any> => {
  let timeoutFn: NodeJS.Timeout;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timeoutFn = setTimeout(() => {
        reject(new Error(`Time limit exceeded ${timeoutMillis}ms`));
      }, timeoutMillis);
    }),
  ])
    .then((v) => {
      clearTimeout(timeoutFn);
      return v;
    })
    .catch((err) => {
      clearTimeout(timeoutFn);
      throw err;
    });
};
export const shortenString = (name: string, maxLength = 20) => {
  const trimmedName = name?.trim();
  if (!trimmedName) return '';
  if (trimmedName.length <= maxLength) return trimmedName;
  return `${trimmedName.slice(0, maxLength)?.trimEnd()}...`;
};
const getDiscountName = (discount: PartOrderDiscount): string => {
  if (discount.flat) {
    return t(
      'Giảm ngay {{FormatCurrency(discountFlat)}} vào giá trị đơn hàng',
      { 'FormatCurrency(discountFlat)': formatCurrency(discount.flat) },
    );
  }
  if (discount.percent) {
    const suffix = discount.maxAmountPerOrder
      ? t(
          '(giảm tối đa trên đơn {{FormatCurrency(discountMaxAmountPerOrder)}})',
          {
            'FormatCurrency(discountMaxAmountPerOrder)': formatCurrency(
              discount.maxAmountPerOrder,
            ),
          },
        )
      : '';
    return (
      t('Giảm ngay {{DiscountPercent}}% tối đa {{var0}}', {
        DiscountPercent: discount.percent,
        var0: formatCurrency(discount.maxAmount),
      }) + suffix
    );
  }
  return '';
};
const getVoucherName = (voucher): string => {
  const defaultVoucherName = t('Tặng Voucher dùng cho đơn hàng sau');
  return !!voucher.name && !!voucher.quantity
    ? `${voucher.quantity} x ${voucher.name}`
    : defaultVoucherName;
};
const getGiftName = (gift: CartGift): string => {
  return `${gift.quantity} x ${gift.name} `;
};
export const getBenefits = (
  benefit?: PartOrderBenefit,
  imgsMap?: GiftsImgMap,
  withDiscount = true,
): PromoItemProps[] => {
  const results: PromoItemProps[] = [];
  if (benefit) {
    if (benefit.discount && withDiscount) {
      results.push({
        type: PromoItemType.DISCOUNT,
        text: getDiscountName(benefit.discount),
      });
    }
    (benefit?.gifts || []).forEach((gift) => {
      results.push({
        type: PromoItemType.GIFT,
        text: getGiftName(gift),
        imgSrc: imgsMap[gift.sku] || gift.imageUrl,
      });
    });
    if (benefit.voucher) {
      results.push({
        type: PromoItemType.VOUCHER,
        text: getVoucherName(benefit.voucher),
      });
    }
  }
  return results;
};
export const getBenefitsList = (
  benefitList?: PartOrderBenefit[],
  imgsMap?: GiftsImgMap,
  withDiscount = true,
): PromoItemProps[] => {
  if (!benefitList && !benefitList.length) return [];
  const nested = benefitList.map((benefit) =>
    getBenefits(benefit, imgsMap, withDiscount),
  );
  return [].concat(...nested);
};
export const getBlockDescription = (
  promo: PartOrderPromotion,
  sellerLines: CartSellerLine[] = [],
): string => {
  const { orderValueMin, orderValueMax } = promo;
  const minValue = formatCurrency(orderValueMin);
  const maxValue = formatCurrency(orderValueMax);
  const appliedSellerName = sellerLines.find(
    ({ seller }) => seller.id === promo?.sellerId,
  )?.seller?.displayName;
  let str = appliedSellerName ? `[${appliedSellerName}]` : '';
  if (orderValueMin && orderValueMax) {
    str += t('Áp dụng cho đơn hàng từ {{minValue}} đến {{maxValue}}', {
      minValue: minValue,
      maxValue: maxValue,
    });
  } else if (orderValueMin) {
    str += t('Áp dụng cho đơn hàng từ {{minValue}}', { minValue: minValue });
  } else if (orderValueMax) {
    str += t('Áp dụng cho đơn hàng tối đa {{maxValue}}', {
      maxValue: maxValue,
    });
  } else str += t('Áp dụng cho mọi đơn hàng');
  return str;
};
export const getBlockDescriptionForCartPromo = (
  promo: CartPromotion,
  sellerLines: CartSellerLine[] = [],
): string => {
  const { condition } = promo.basePromotion;
  const { orderValueMin, orderValueMax } = condition;
  const minValue = formatCurrency(orderValueMin);
  const maxValue = formatCurrency(orderValueMax);
  const appliedSellerName = sellerLines.find(
    ({ seller }) => seller.id === promo?.sellerId,
  )?.seller?.displayName;
  let str = appliedSellerName ? `[${appliedSellerName}]` : '';
  if (orderValueMin && orderValueMax) {
    str += t('Áp dụng cho đơn hàng từ {{minValue}} đến {{maxValue}}', {
      minValue: minValue,
      maxValue: maxValue,
    });
  } else if (orderValueMin) {
    str += t('Áp dụng cho đơn hàng từ {{minValue}}', { minValue: minValue });
  } else if (orderValueMax) {
    str += t('Áp dụng cho đơn hàng tối đa {{maxValue}}', {
      maxValue: maxValue,
    });
  } else str += t('Áp dụng cho mọi đơn hàng');
  return str;
};
export const isVnpay = (partner: string): boolean =>
  [
    'vietin',
    'vcb',
    'vnmart',
    'icheck',
    'agri',
    'bidv',
    'vp',
    /* 'tripi', */
    /* 'dinogo', */
    'ivb',
    'exim',
    'abb',
    // do not change priority of sacombank pay and sacombankmb
    'sacombankmb',
    'sacombank',
    /* 'wecare', */
    /* 'agent', */
    'saigonbank',
    'namabank',
    'bidc',
    'vietabank',
    'kienlongbank',
    'vnptpay',
  ].includes(partner);
export const getComboPromotionId = (cart: CartContextState): number => {
  const comboPromotions = (cart.order?.promotions || []).find(
    (promotion) => promotion.applyType === 'combo',
  );
  return comboPromotions ? comboPromotions.id : null;
};
export const getComboLineItems = (comboProducts: CartOrderLine[]) => {
  const nestedCombo = comboProducts.map((product) =>
    product.childItems.map(({ sku, quantity }) => {
      return {
        sku,
        quantity: Number(quantity / product.quantity),
      };
    }),
  );
  return [].concat(...nestedCombo);
};
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};
export const removeUnicode = (str: string) => {
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
  str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
  str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
  str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
  str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
  str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
  str = str.replace(/Đ/g, 'D');
  return str;
};
